// Thin wrappers around the director-only /api/miniapp/* endpoints (M-3).
import { apiRequest } from './client'
import type {
  AiReportsResponse,
  CreateTaskInput,
  CreateTaskResponse,
  CriticalDealsResponse,
  CriticalFilter,
  DashboardPeriod,
  DashboardResponse,
  DeleteTaskResponse,
  DirectorNoteResponse,
  LocalMemberInviteResponse,
  ParseTaskResult,
  ReassignResponse,
  RemoveLocalMemberResponse,
  Role,
  TeamMemberDetailResponse,
  TeamResponse,
  UpdateLocalMemberResponse,
  WorkHoursConfig,
} from './types'

export function fetchDashboard(period: DashboardPeriod = 'today'): Promise<DashboardResponse> {
  return apiRequest<DashboardResponse>(`/api/miniapp/dashboard?period=${period}`)
}

export function fetchTeam(): Promise<TeamResponse> {
  return apiRequest<TeamResponse>('/api/miniapp/team')
}

export function fetchTeamMember(managerId: number): Promise<TeamMemberDetailResponse> {
  return apiRequest<TeamMemberDetailResponse>(`/api/miniapp/team/${managerId}`)
}

// ── local employees (Telegram-only members; director-only) ───────────────────

// Create a local member + mint a one-time invite link to send via Telegram.
export function addLocalMember(name: string): Promise<LocalMemberInviteResponse> {
  return apiRequest<LocalMemberInviteResponse>('/api/miniapp/team/local', {
    method: 'POST',
    body: { name },
  })
}

// Re-mint a fresh invite link for an existing local member.
export function remintLocalInvite(memberId: number): Promise<LocalMemberInviteResponse> {
  return apiRequest<LocalMemberInviteResponse>(`/api/miniapp/team/local/${memberId}/invite`, {
    method: 'POST',
    body: {},
  })
}

// Remove a local member (cancels their open tasks).
export function removeLocalMember(memberId: number): Promise<RemoveLocalMemberResponse> {
  return apiRequest<RemoveLocalMemberResponse>(`/api/miniapp/team/local/${memberId}`, {
    method: 'DELETE',
  })
}

// Edit a local member's role and/or Telegram chat id. Only the fields passed
// change; telegram_chat_id: '' clears the binding.
export function updateLocalMember(
  memberId: number,
  patch: { role?: Role; telegram_chat_id?: string },
): Promise<UpdateLocalMemberResponse> {
  return apiRequest<UpdateLocalMemberResponse>(`/api/miniapp/team/local/${memberId}`, {
    method: 'PATCH',
    body: patch,
  })
}

export function fetchCriticalDeals(filter: CriticalFilter = 'all'): Promise<CriticalDealsResponse> {
  return apiRequest<CriticalDealsResponse>(`/api/miniapp/critical-deals?filter=${filter}`)
}

export function fetchAiReports(): Promise<AiReportsResponse> {
  return apiRequest<AiReportsResponse>('/api/miniapp/ai-reports')
}

// ── slice-3 director actions ────────────────────────────────────────────────

export function createTask(input: CreateTaskInput): Promise<CreateTaskResponse> {
  return apiRequest<CreateTaskResponse>('/api/miniapp/tasks', { method: 'POST', body: input })
}

// AI mode: free-form instruction → structured task draft (design parity).
export function parseTask(text: string): Promise<ParseTaskResult> {
  return apiRequest<ParseTaskResult>('/api/miniapp/director/parse-task', { method: 'POST', body: { text } })
}

export function reassignTask(
  taskId: number,
  newManagerId: number,
  comment?: string,
): Promise<ReassignResponse> {
  return apiRequest<ReassignResponse>(`/api/miniapp/tasks/${taskId}/reassign`, {
    method: 'POST',
    body: { new_manager_id: newManagerId, comment: comment ?? null },
  })
}

export function setDirectorNote(taskId: number, text: string): Promise<DirectorNoteResponse> {
  return apiRequest<DirectorNoteResponse>(`/api/miniapp/tasks/${taskId}/director_note`, {
    method: 'POST',
    body: { text },
  })
}

// Director soft-deletes a task (status → cancelled; closes the linked Bitrix task).
export function deleteTask(taskId: number): Promise<DeleteTaskResponse> {
  return apiRequest<DeleteTaskResponse>(`/api/miniapp/tasks/${taskId}/delete`, {
    method: 'POST',
    body: {},
  })
}

// ── company working hours (director-only) ────────────────────────────────────

export function fetchWorkHours(): Promise<WorkHoursConfig> {
  return apiRequest<WorkHoursConfig>('/api/miniapp/director/work-hours')
}

export function saveWorkHours(payload: WorkHoursConfig): Promise<WorkHoursConfig> {
  return apiRequest<WorkHoursConfig>('/api/miniapp/director/work-hours', {
    method: 'PATCH',
    body: payload,
  })
}
